import Offer from "domain/offer";
import { useEffect, useMemo, useReducer, useState } from "react";
import * as _ from "lodash";

export enum OfferField {
    Type,
    CustomerAccount,
    CustomerWill,
    Commodity,
    ContractExtendedType,
    ContractLocation,
    InventoryLocation,
    Location,
    DeliveryMethod,
    DeliveryPeriod,
    StartDate,
    EndDate,
    Symbol,
    Basis,
    SetBasis,
    OrderType,
    CashPrice,
    Quantity,
    Expiration,
    FinalPricingDate,
    Trader,
    Comment,
    DeliveryTerms,
    Region,
    DPContract,
    FreightFee,
    Fees,
    PriceAtOpen,
    FuturesPrice,
    AtMarket,
    TasFallbackOption,
    PaymentOption,
    ContractSubType,
    InterestStartDate,
    PayDate,
    CustomerContractId,
    ErpContractId,
    NotifyCustomer,
    PlaceHedge,
    DisableErpSync,
    InterestRate,
    UseUnappliedBalance
}

interface ReducerParams {
    field: OfferField,
    newState: Partial<Offer>,
    form: OfferFormState,
    isInitialize?: boolean,
    isUpdate?: boolean,
}

type ChildReducer = (state: Offer, form?: OfferFormState, previousState?: Offer) => Offer;

export interface OfferFormState {
    displayDeliveryTerms?: boolean;
    displayCustomerContractId?: boolean;
    placeHedgeOrder?: boolean;
    hasUserUpdatedCashPrice?: boolean;
    shouldShowHedgeOrderToggle?: boolean;
    isPricingDelayedPriceContract?: boolean;
    lockDeliveryMethod?: boolean;
}

export interface ReducerDefinition {
    reducer: ChildReducer;
    fields: OfferField[];
    runOnInitialization?: boolean;
    excludeForUpdates?: boolean;
    excludeForUpdateInitialization?: boolean;
}

export interface OfferBuilder {
    offer: Offer;
    updateOffer(field: OfferField | null, val: Partial<Offer>, newFormState?: OfferFormState): void;
    formState: OfferFormState;
    updateForm(formState: Partial<OfferFormState>): void;
}

export default function useOfferBuilder(defaultOffer: Offer, reducers: ReducerDefinition[], initialState: OfferFormState, isUpdate: boolean): OfferBuilder {
    const reducerCollection = useMemo(() => {
        return _.groupBy(
            reducers
                .flatMap(def => def.fields.map(f => ({ key: f as number, reducer: def.reducer }))),
            m => m.key);
    }, [reducers]);

    const [formState, setFormState] = useState<OfferFormState>(initialState);

    const [offer, dispatch] = useReducer(mainOfferReducer, defaultOffer);

    useEffect(() => {
        dispatch({ newState: defaultOffer, form: initialState, field: null, isInitialize: true, isUpdate })
    }, [defaultOffer]);

    function mainOfferReducer(oldState: Offer, params: ReducerParams): Offer {
        const toRun = params.isInitialize
            ? reducers.filter(m => m.runOnInitialization && (!params.isUpdate || !m.excludeForUpdateInitialization)).map(m => m.reducer)
            : reducerCollection[params.field]?.map(m => m.reducer);

        //If we don't have special handling for the update, use the passed state
        if (toRun == null) return { ...oldState, ...params.newState };

        return runReducers(params, toRun, oldState);
    }

    function runReducers(params: ReducerParams, reducers: ChildReducer[], oldState: Offer): Offer {
        let s = { ...oldState, ...params.newState };
        for (const r of reducers) {
            s = r(s, params.form, oldState);
        }
        setFormState(params.form);
        return s;
    }

    function updateOffer(field: OfferField | null, val: Partial<Offer>, newFormState?: OfferFormState) {
        dispatch({
            field,
            newState: val,
            form: { ...formState, ...newFormState },
            isUpdate,
        });
    }

    return {
        offer,
        updateOffer,
        formState,
        updateForm: p => setFormState(m => ({ ...m, ...p }))
    };
}
